import React from "react"

import type {
  BucketClickData,
  DashboardWidget,
  WidgetFillerContentProps,
} from "../types/core"
import type { WidgetProps } from "./BaseWidget"
import { WidgetRegistry } from "./WidgetRegistry"

type WidgetRenderProps = WidgetProps & {
  metric?: unknown
  onPointClick?: (point: BucketClickData) => void
  onChartClick?: (point: BucketClickData) => void
  selectedPointInChart?: BucketClickData | null
  onSplitKeysChange?: (splitKeys: string[]) => void
}

export const WidgetFillerContent: React.FC<WidgetFillerContentProps> = ({
  type,
  widget,
  onChartClick,
  onPointClick,
  selectedPointInChart,
  onSplitKeysChange,
  onExportDataReady,
}) => {
  const widgetClass = WidgetRegistry.getWidget(type)

  if (!widgetClass) {
    return (
      <div className="h-full flex items-center justify-center text-coval-text-muted">
        <div className="text-center">
          <div className="w-16 h-16 bg-surface-1 rounded-lg flex items-center justify-center mb-2 mx-auto">
            <span className="text-2xl">📊</span>
          </div>
          <p className="text-sm text-coval-text-muted">Unknown Widget Type</p>
        </div>
      </div>
    )
  }

  // Create a mock widget object if not provided
  const mockWidget: DashboardWidget = widget || {
    id: "preview",
    type,
    title: widgetClass.title,
  }

  // Create widget props - widgets should use DashboardFiltersContext directly
  const widgetProps: WidgetRenderProps = {
    widget: mockWidget,
    metric: (widget as (DashboardWidget & { metric?: unknown }) | null)?.metric,
  }

  // Add point click functionality for chart widgets
  if (type === "chart" && widget && onPointClick) {
    const metricId = widget.config?.metricId
    widgetProps.onPointClick = (point: BucketClickData) =>
      onPointClick(point, widget.id, metricId)
    widgetProps.selectedPointInChart = selectedPointInChart
  }

  if (type === "chart" && widget && onChartClick) {
    const metricId = widget.config?.metricId
    widgetProps.onChartClick = (point: BucketClickData) =>
      onChartClick(point, widget.id, metricId)
  }

  // Add split keys callback for chart widgets
  if (type === "chart" && onSplitKeysChange) {
    widgetProps.onSplitKeysChange = onSplitKeysChange
  }

  // Add export data callback for chart, table, and assignee call volume widgets
  if (
    (type === "chart" ||
      type === "table" ||
      type === "assignee_call_volume" ||
      type === "assignee_call_history") &&
    onExportDataReady
  ) {
    widgetProps.onExportDataReady = onExportDataReady
  }

  return widgetClass.renderWidget(widgetProps)
}
